<template>
  <div class="layout-container">
    <!-- 顶部导航栏 -->
    <header class="header">
      <div class="header-left">
        <el-button 
          v-if="isMobile" 
          icon="el-icon-menu" 
          type="text" 
          class="menu-toggle-btn"
          @click="toggleSidebar"
        ></el-button>
        <span class="logo">
          农化客户管理平台
          <span class="logo-sub">NH AGRO CRM</span>
        </span>
      </div>
      <div class="header-right">
        <el-dropdown
          ref="userDropdown"
          trigger="click"
          :hideOnClick="true"
          @command="handleUserCommand"
        >
          <span class="user-info">
            <i class="el-icon-user"></i>
            {{ userInfo.name }} <i class="el-icon-arrow-down"></i>
          </span>
          <el-dropdown-menu slot="dropdown">
            <!-- 使用 command 让下拉菜单在触发后自动关闭（避免退出登录后 popper 悬浮在登录页） -->
            <el-dropdown-item command="password">修改密码</el-dropdown-item>
            <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </header>

    <!-- 修改当前登录用户密码（与用户管理一致） -->
    <el-dialog
      custom-class="layout-password-dialog"
      title="修改密码"
      :visible.sync="passwordDialogVisible"
      :width="isMobile ? '90%' : '400px'"
      :append-to-body="true"
      @closed="resetPasswordForm"
    >
      <el-form :model="passwordForm" :rules="passwordRules" ref="passwordForm" label-width="100px">
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="passwordForm.newPassword" type="password" placeholder="请输入新密码" show-password></el-input>
          <div class="password-strength">
            <div class="strength-bars">
              <span :class="['bar', passwordStrengthLevel >= 1 ? 'active' : '']"></span>
              <span :class="['bar', passwordStrengthLevel >= 2 ? 'active' : '']"></span>
              <span :class="['bar', passwordStrengthLevel >= 3 ? 'active' : '']"></span>
            </div>
            <span class="strength-text" :class="'level-' + passwordStrengthTextType">
              密码强度：{{ passwordStrengthText }}
            </span>
          </div>
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请确认新密码" show-password></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handlePasswordSubmit">提交</el-button>
      </span>
    </el-dialog>
    
    <!-- 主体内容区 -->
    <div class="main-container">
      <!-- 侧边栏菜单 -->
      <aside :class="['sidebar', {'sidebar-mobile': isMobile, 'sidebar-hidden': isMobile && !sidebarVisible}]">
        <el-menu 
          :default-active="activeMenu" 
          class="el-menu-vertical-demo" 
          @select="handleMenuSelect"
          :collapse="isMobile ? false : null"
        >
          <el-menu-item
            v-for="menu in sideMenus"
            :key="menu.id"
            :index="menu.path"
          >
            <i :class="menu.icon || 'el-icon-menu'"></i>
            <span slot="title">{{ menu.name }}</span>
          </el-menu-item>
        </el-menu>
      </aside>
      
      <!-- 遮罩层 -->
      <div 
        v-if="isMobile && sidebarVisible" 
        class="mobile-mask" 
        @click="toggleSidebar"
      ></div>
      
      <!-- 内容区域 -->
      <main class="content">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import { getUserInfo, clearAuth } from '../utils/auth'
import { setAllowedPaths, clearAllowedPaths } from '../utils/permission'
import request from '../utils/request'

export default {
  name: 'Layout',
  data() {
    return {
      userInfo: getUserInfo(),
      // 高亮当前完整路由路径，确保类似 /customers 与 /customers/recycle-bin 分开高亮
      activeMenu: this.$route.path || '/home',
      isMobile: false,
      sidebarVisible: false,
      sideMenus: [],
      passwordDialogVisible: false,
      passwordForm: {
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {}
    }
  },
  created() {
    this.passwordRules = {
      newPassword: [
        { validator: this.validateStrongPassword, trigger: 'blur' }
      ],
      confirmPassword: [
        { required: true, message: '请确认新密码', trigger: 'blur' },
        { validator: this.validateConfirmPassword, trigger: 'blur' }
      ]
    }
  },
  mounted() {
    this.checkScreenSize()
    this.loadSideMenus()
    window.addEventListener('resize', this.checkScreenSize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize)
  },
  watch: {
    $route(to) {
      this.activeMenu = to.path || '/home'
      // 切换路由后强制收起顶部用户下拉，避免移动端/弹出窗叠层导致下拉残留
      this.$nextTick(() => this.closeUserDropdown())
    }
  },
  computed: {
    passwordStrengthLevel() {
      return this.calcPasswordStrength(this.passwordForm.newPassword).level
    },
    passwordStrengthText() {
      return this.calcPasswordStrength(this.passwordForm.newPassword).text
    },
    passwordStrengthTextType() {
      return this.calcPasswordStrength(this.passwordForm.newPassword).type
    }
  },
  methods: {
    closeUserDropdown() {
      const ref = this.$refs.userDropdown
      if (!ref) return
      // ElDropdown 内部方法 hide() 用于关闭弹层
      if (typeof ref.hide === 'function') {
        ref.hide()
      }
    },
    handleUserCommand(command) {
      if (command === 'password') {
        this.handleOpenPasswordDialog()
        return
      }
      if (command === 'logout') {
        // 退出登录时强制关闭弹窗，避免 append-to-body 的内容残留
        this.passwordDialogVisible = false
        this.passwordForm.newPassword = ''
        this.passwordForm.confirmPassword = ''
        this.handleLogout()
      }
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth < 768
      if (!this.isMobile) {
        this.sidebarVisible = true // 桌面端始终显示侧边栏
      }
    },
    handleMenuSelect(key) {
      this.$router.push(key)
      if (this.isMobile) {
        this.sidebarVisible = false // 移动端选中菜单后隐藏侧边栏
      }
      // 点击侧边栏后也强制收起顶部用户下拉
      this.$nextTick(() => this.closeUserDropdown())
    },
    handleOpenPasswordDialog() {
      const u = getUserInfo()
      if (!u || !u.id) {
        this.$message.error('无法获取当前用户信息，请重新登录')
        return
      }
      this.userInfo = u
      this.passwordForm.newPassword = ''
      this.passwordForm.confirmPassword = ''
      this.passwordDialogVisible = true
      this.$nextTick(() => {
        this.$refs.passwordForm && this.$refs.passwordForm.clearValidate()
      })
    },
    resetPasswordForm() {
      this.passwordForm = { newPassword: '', confirmPassword: '' }
      this.$refs.passwordForm && this.$refs.passwordForm.resetFields()
    },
    validateStrongPassword(rule, value, callback) {
      if (!value) {
        callback(new Error('请输入密码'))
        return
      }
      if (value.length < 6 || value.length > 20) {
        callback(new Error('密码长度需在 6 到 20 个字符之间'))
        return
      }
      const hasLetter = /[A-Za-z]/.test(value)
      const hasNumber = /\d/.test(value)
      const hasSymbol = /[^A-Za-z0-9]/.test(value)
      if (!hasLetter || !hasNumber || !hasSymbol) {
        callback(new Error('密码必须同时包含字母、数字和符号'))
      } else {
        callback()
      }
    },
    validateConfirmPassword(rule, value, callback) {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    },
    calcPasswordStrength(password) {
      if (!password) {
        return { level: 0, text: '无', type: 'none' }
      }
      let score = 0
      const length = password.length
      const hasLetter = /[A-Za-z]/.test(password)
      const hasNumber = /\d/.test(password)
      const hasSymbol = /[^A-Za-z0-9]/.test(password)
      if (length >= 6) score++
      if (length >= 10) score++
      if (hasLetter) score++
      if (hasNumber) score++
      if (hasSymbol) score++
      let level = 1
      let text = '弱'
      let type = 'low'
      if (score >= 4) {
        level = 3
        text = '强'
        type = 'high'
      } else if (score >= 3) {
        level = 2
        text = '中'
        type = 'medium'
      }
      return { level, text, type }
    },
    handlePasswordSubmit() {
      const u = getUserInfo()
      if (!u || !u.id) {
        this.$message.error('无法获取当前用户信息')
        return
      }
      this.$refs.passwordForm.validate((valid) => {
        if (!valid) return
        request.put(`/users/${u.id}/password`, {
          password: this.passwordForm.newPassword
        })
          .then(() => {
            this.$message.success('密码修改成功')
            this.passwordDialogVisible = false
          })
          .catch(() => {
            this.$message.error('密码修改失败')
          })
      })
    },
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        clearAuth()
        clearAllowedPaths()
        this.$router.push('/login')
        this.$message.success('退出登录成功')
      }).catch(() => {
        // 取消退出登录
      })
    },
    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible
    },
    // 根据当前用户角色加载可见菜单，并写入权限缓存供路由守卫使用
    loadSideMenus() {
      request.get('/menus/current')
        .then(res => {
          const menus = res.data || res
          this.sideMenus = menus || []
          const paths = (menus || []).map(m => m.path).filter(Boolean)
          setAllowedPaths(paths)
        })
        .catch(err => {
          console.error('加载侧边菜单失败:', err)
        })
    }
  }
}
</script>

<style scoped>
.layout-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

/* 顶部导航栏 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
  background-image: linear-gradient(120deg, #0f9bd7 0%, #17b676 45%, #0f8f59 100%);
  color: #fff;
  padding: 0 24px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  position: relative;
  z-index: 10;
}

.header::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 10% 0, rgba(255, 255, 255, 0.18), transparent 55%),
              radial-gradient(circle at 90% 100%, rgba(255, 255, 255, 0.16), transparent 55%);
  pointer-events: none;
}

.header-left {
  display: flex;
  align-items: center;
}

.menu-toggle-btn {
  color: #fff;
  margin-right: 10px;
}

.logo {
  display: flex;
  flex-direction: column;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 1px;
}

.logo-sub {
  font-size: 11px;
  opacity: 0.85;
  letter-spacing: 2px;
}

.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 10px;
  height: 40px;
  line-height: 40px;
  border-radius: 4px;
  transition: background-color 0.3s, color 0.3s, box-shadow 0.3s;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background-color: rgba(0, 0, 0, 0.08);
}

.user-info:hover {
  background-color: rgba(0, 0, 0, 0.18);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.55);
}

.user-info i {
  margin-right: 8px;
}

/* 主体内容区 */
.main-container {
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
}

/* 侧边栏菜单 */
.sidebar {
  width: 200px;
  background: linear-gradient(180deg, #022b3a 0%, #053b4c 40%, #0b4f54 100%);
  color: #e0f2f1;
  overflow-y: auto;
  transition: all 0.3s ease;
}

.sidebar-mobile {
  position: fixed;
  top: 60px;
  left: -200px;
  width: 200px;
  height: calc(100vh - 60px);
  z-index: 1000;
  box-shadow: 2px 0 6px rgba(0, 21, 41, 0.35);
}

.sidebar-hidden {
  left: -200px;
}

.sidebar:not(.sidebar-hidden) {
  left: 0;
}

.sidebar .el-menu {
  border-right: none;
  background-color: transparent;
  color: inherit;
}

.sidebar .el-menu-item {
  color: #e0f2f1;
  height: 60px;
  line-height: 60px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.sidebar .el-menu-item:hover {
  background-color: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.sidebar .el-menu-item.is-active {
  background: linear-gradient(120deg, #17b676, #0f9bd7);
  color: #fff;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

/* 遮罩层 */
.mobile-mask {
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

/* 内容区域 */
.content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: transparent;
  transition: all 0.3s ease;
}

@media (max-width: 768px) {
  .layout-container {
    position: relative;
  }
  
  .header {
    padding: 0 12px;
    height: 56px;
  }
  
  .logo {
    font-size: 18px;
  }
  
  .main-container {
    flex: 1;
  }
  
  .content {
    padding: 10px;
    width: 100%;
  }
}
</style>

<!-- 弹窗挂载到 body，需单独写类名前缀，避免污染全局 -->
<style>
.layout-password-dialog .password-strength {
  margin-top: 6px;
}
.layout-password-dialog .password-strength .strength-bars {
  display: flex;
  gap: 4px;
  margin-bottom: 2px;
}
.layout-password-dialog .password-strength .bar {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background-color: #ebeef5;
  transition: background-color 0.3s;
}
.layout-password-dialog .password-strength .bar.active:nth-child(1) {
  background-color: #f56c6c;
}
.layout-password-dialog .password-strength .bar.active:nth-child(2) {
  background-color: #e6a23c;
}
.layout-password-dialog .password-strength .bar.active:nth-child(3) {
  background-color: #67c23a;
}
.layout-password-dialog .password-strength .strength-text {
  font-size: 12px;
}
.layout-password-dialog .password-strength .strength-text.level-low {
  color: #f56c6c;
}
.layout-password-dialog .password-strength .strength-text.level-medium {
  color: #e6a23c;
}
.layout-password-dialog .password-strength .strength-text.level-high {
  color: #67c23a;
}
</style>